import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import { APIClient } from "../../helpers/api_helper";
import { translateApiError } from "../../helpers/translateApiError";
import BreadCrumb from "../../components/Common/BreadCrumb";
import SmsState from "../../components/Common/SmsState";
import formatPersonName from "../../helpers/formatPersonName";
import { formatDateTime } from "../../helpers/dateFormat";
import "./smsLogDetail.css";

// Must match DR_TIMEOUT_MESSAGE in the backend (delivery-status.service.ts)
const DR_TIMEOUT_MESSAGE = "電信未回報";

const api = new APIClient();

const SmsLogDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [smsLog, setSmsLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSmsLogDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = `${process.env.REACT_APP_API_BASE_URL}/admin/sms-logs/${id}`;
      const response = await api.get(url);
      if (response?.data) {
        setSmsLog(response.data);
      }
    } catch (err) {
      console.error("Error fetching SMS log detail:", err);

      // Only set error if it's not an auto logout error
      if (!err?.autoLogout) {
        setError(translateApiError(err, t("smsLogs.detail.loadError")));
      }
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchSmsLogDetail();
  }, [fetchSmsLogDetail]);

  // Back to the list page and filters the user came from.
  const handleBack = () => {
    history.push(history.location.state?.from || "/sms-logs");
  };

  const getStatusState = (status, message) => {
    // Treat null/empty as Pending
    if (!status) {
      return "Pending";
    }
    const statusLower = status.toLowerCase();
    if (statusLower === "delivered" || statusLower === "success") {
      return "Delivered";
    } else if (statusLower === "pending" || statusLower === "unknown") {
      // No delivery report 24 hours after sending (HXSMSF-147)
      return statusLower === "unknown" && message === DR_TIMEOUT_MESSAGE
        ? "NoReport"
        : "Pending";
    } else if (statusLower === "not_sent") {
      return "NotSent";
    }
    return "Failed";
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("smsLogs.detail.pageTitle")}
            pageTitle={t("smsLogs.pageTitle")}
          />
          <div className="text-center mt-5">
            <Spinner color="primary" />
            <p className="mt-2">{t("smsLogs.detail.loading")}</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !smsLog) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("smsLogs.detail.pageTitle")}
            pageTitle={t("smsLogs.pageTitle")}
          />
          <Alert color="danger">
            {error || t("smsLogs.detail.notFound")}
            <Button color="link" onClick={handleBack} className="ms-2">
              {t("smsLogs.detail.goBack")}
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  document.title = t("common.pageTitle", {
    page: t("smsLogs.detail.pageTitle"),
  });

  const recipientName =
    formatPersonName(smsLog.recipientsFirstName, smsLog.recipientsLastName) ||
    "-";

  const statusState = getStatusState(
    smsLog.deliveryStatus,
    smsLog.deliveryMessage
  );
  const notSent = statusState === "NotSent";
  const dash = "—";

  // HXSMSF-147: the reason under the status. Not sent: our error code.
  // Failed: the carrier's report first, then EVERY8D's error.
  const reasonByState = {
    NotSent: t(`smsLogs.errorCodes.${smsLog.errorCode}`, {
      defaultValue: t("smsLogs.errorCodes.UNKNOWN"),
    }),
    Failed:
      smsLog.deliveryMessage ||
      smsLog.errorMessage ||
      t("smsLogs.detail.reasons.failedNoReason"),
    Delivered: t("smsLogs.detail.reasons.delivered"),
    Pending: t("smsLogs.detail.reasons.pending"),
    NoReport: t("smsLogs.detail.reasons.noReport"),
  };
  const reason = reasonByState[statusState];
  const reasonClass = {
    NotSent: "sms-reason-not-sent",
    Delivered: "sms-reason-ok",
    Pending: "sms-reason-wait",
    NoReport: "sms-reason-wait",
  }[statusState];
  // EVERY8D rejected it: also show EVERY8D's own message under our reason
  const every8dMessage =
    notSent && smsLog.errorCode === "EVERY8D_REJECTED"
      ? smsLog.errorMessage
      : null;

  // What the carrier reported, once there is a report (or it timed out)
  const hasReport = Boolean(smsLog.deliveryCode) || statusState === "NoReport";
  const carrierResult = notSent || smsLog.state === "FAILED"
    ? dash
    : hasReport
    ? smsLog.deliveryMessage || dash
    : t("smsLogs.detail.values.notReported");

  const sendStatus = {
    SUCCESS: t("smsLogs.detail.values.sentToPlatform"),
    NOT_SENT: t("smsLogs.detail.values.notSent"),
    FAILED: t("smsLogs.detail.values.sendFailed"),
  }[smsLog.state] || dash;

  // null until a final delivery report: "待回報"
  const charge =
    smsLog.deliveryCharge === null || smsLog.deliveryCharge === undefined ? (
      t("smsLogs.detail.values.chargePending")
    ) : (
      <>
        {smsLog.deliveryCharge}
        {smsLog.deliveryCharge === 0 && (notSent || statusState === "Failed") && (
          <span className="sms-info-note">
            {t(
              notSent
                ? "smsLogs.detail.values.notSentNoCharge"
                : "smsLogs.detail.values.notDeliveredNoCharge"
            )}
          </span>
        )}
      </>
    );

  // Four groups: recipient, delivery, charge, system. [label, value, full row]
  const groups = [
    [
      [
        t("smsLogs.columns.recipientName"),
        smsLog.contactId && smsLog.portalId ? (
          <a
            key="contact"
            href={`https://app.hubspot.com/contacts/${smsLog.portalId}/contact/${smsLog.contactId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {recipientName}
          </a>
        ) : (
          recipientName
        ),
      ],
      [t("smsLogs.columns.recipientPhone"), smsLog.phoneNumber || dash],
      [
        t("smsLogs.detail.sentTime"),
        smsLog.sentAt ? formatDateTime(smsLog.sentAt, i18n.language) : dash,
      ],
      [
        t("smsLogs.columns.workflowId"),
        smsLog.workflowId ? (
          <a
            key="workflow"
            href={smsLog.workflowTitle}
            target="_blank"
            rel="noopener noreferrer"
          >
            {smsLog.workflowId}
          </a>
        ) : (
          dash
        ),
      ],
    ],
    [
      [t("smsLogs.detail.fields.sendStatus"), sendStatus],
      [t("smsLogs.detail.fields.carrierResult"), carrierResult],
      [
        t("smsLogs.detail.fields.reportedAt"),
        smsLog.deliveryReceivedAt
          ? formatDateTime(smsLog.deliveryReceivedAt, i18n.language)
          : dash,
      ],
    ],
    [[t("smsLogs.detail.fields.charge"), charge, true]],
    [[t("smsLogs.columns.smsId"), smsLog.id, true]],
  ];

  const renderGroup = (fields, index) => (
    <div
      key={index}
      className={`sms-info-group ${
        index === groups.length - 1 ? "sms-info-system" : ""
      }`}
    >
      <Row className="g-3">
        {fields.map(([label, value, full]) => (
          <Col key={label} xs={12} sm={full ? 12 : 6}>
            <p className="sms-info-label mb-1">{label}</p>
            <div className="sms-info-value">{value}</div>
          </Col>
        ))}
      </Row>
    </div>
  );

  return (
    <div className="page-content sms-log-detail">
      <Container fluid>
        <BreadCrumb
          title={t("smsLogs.detail.pageTitle")}
          pageTitle={t("smsLogs.pageTitle")}
        />

        <Button
          color="link"
          onClick={handleBack}
          className="back-button p-0 mb-3"
        >
          <i className="ri-arrow-left-line"></i>
          <span>{t("common.actions.back")}</span>
        </Button>

        <Row className="g-4 align-items-start">
          {/* Message body */}
          <Col lg={7}>
            <Card className="mb-0">
              <CardHeader>
                <h5 className="card-title mb-0">
                  {t("smsLogs.detail.messageBody")}
                </h5>
              </CardHeader>
              <CardBody>
                <div className="sms-message-text">
                  {smsLog.body || smsLog.workflowMessage || "-"}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* SMS information */}
          <Col lg={5}>
            <Card className="mb-0">
              {/* Result first: status, then why it was not delivered */}
              <CardHeader>
                <SmsState state={statusState} size="Medium" />
                {reason && (
                  <div
                    className={`sms-reason mt-3 ${reasonClass || ""}`}
                    role="note"
                    aria-label={t("smsLogs.detail.reason")}
                  >
                    <i className="ri-error-warning-line"></i>
                    <span>
                      {reason}
                      {every8dMessage && (
                        <span className="d-block mt-1">
                          {t("smsLogs.detail.every8dMessage", {
                            message: every8dMessage,
                          })}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardBody>{groups.map(renderGroup)}</CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SmsLogDetail;
